import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { ReduxState } from "..";
import {
  cloneTagRegexesSet,
  createTagRegexes,
  createTagRegexesSet,
  createTagRegexWithSet,
  createTagRule,
  getCategorizedTransactions,
  getCategorizedTransactionsWithTagLevels,
  getTagRegexes,
  getTaxRegexesByType,
  updateTagRegexes,
  updateTagRule,
} from "../../docuclipper/api";
import {
  DocType,
  TagCategoryRule,

  TagRegexes,
  TagRegexesSets,
  TagRegexesType,
  TagRule,
} from "../../docuclipper/DocuclipperTypes";
import { createAlert } from "./Alerts";
import { randomColor, setByCategoryKeyword, setCategories } from "./Categories";
import { setTls } from "./TransactionManager";
import { deleteTagRegexes } from '../../docuclipper/api';

const initialState: {
  loading: boolean;
  pieLoading: boolean;
  tagRegexes: TagRegexes[];
  tagRules: TagRule[];
  tagRegexesSets: TagRegexesSets[];
  selectedTagRegexesSet: TagRegexesSets | null;
  byCategoryKeyword: any[];
} = {
  loading: false,
  pieLoading: false,
  tagRegexes: [],
  tagRules: [],
  tagRegexesSets: [],
  selectedTagRegexesSet: null,
  byCategoryKeyword: [],
};

const slice = createSlice({
  name: "TagRegexes",
  initialState,
  reducers: {
    setLoading(state, action: PayloadAction<boolean>) {
      state.loading = action.payload;
    },
    setPieLoading(state, action: PayloadAction<boolean>) {
      state.pieLoading = action.payload;
    },
    setTagRegexes(state, action: PayloadAction<TagRegexes[]>) {
      state.tagRegexes = action.payload;
    },
    addTagRegexes(state, action: PayloadAction<TagRegexes>) {
      state.tagRegexes = [...state.tagRegexes, action.payload];
    },
    addTagRule2(state, action: PayloadAction<TagRule>) {
      state.tagRules = [...state.tagRules, action.payload];
    },
    addTagRegexesSet(state, action: PayloadAction<TagRegexesSets>) {
      state.tagRegexesSets = [...state.tagRegexesSets, action.payload];
    },
    updateTagRegexes2(state, action: PayloadAction<{ id; tag; tagLabel; regexes, jsonRules }>) {
      state.tagRegexes = state.tagRegexes.map((x) =>
        x.id === action.payload.id
          ? {
            ...x,
            tag: action.payload.tag,
            tagLabel: action.payload.tagLabel,
            regexes: action.payload.regexes,
            jsonRules: action.payload.jsonRules
          }
          : x
      );
    },
    updateTagRule2(state, action: PayloadAction<TagRule>) {
      state.tagRules = state.tagRules.map((x) =>
        x.id === action.payload.id ? { ...x, ...action.payload } : x
      );
    },
    setTagRegexesSets(state, action: PayloadAction<TagRegexesSets[]>) {
      const nextSets = action.payload;
      // Keep current selection by name when refetching (e.g. after add rule /
      // recategorize), so the category group doesn’t change inadvertently.
      if (state.selectedTagRegexesSet) {
        const match = nextSets.find(
          (s) => s.name === state.selectedTagRegexesSet!.name
        );
        state.selectedTagRegexesSet = match ?? null;
      }
      state.tagRegexesSets = nextSets;
      // Do not auto-select first set here; TagRegexes.tsx restores from
      // docuclipper.ui.selectedCategoryGroup (localStorage) on load.
    },
    setSelectedTagRegexesSet(state, action: PayloadAction<string>) {
      const matches = state.tagRegexesSets.filter(
        (x) => x.name === action.payload
      );
      if (matches.length > 0) {
        state.selectedTagRegexesSet = matches[0];
      } else {
        state.selectedTagRegexesSet = null;
      }
    },
    setByCategoryKeyword(state, action: PayloadAction<any[]>) {
      state.byCategoryKeyword = action.payload;
    },
  },
});

export const {
  setLoading,
  setPieLoading,
  setTagRegexes,
  setTagRegexesSets,
  setSelectedTagRegexesSet,
  addTagRegexes,
  addTagRegexesSet,
  updateTagRegexes2,
  updateTagRule2,
  addTagRule2,

} = slice.actions;

export const fetchTagRegexes =
  (type: TagRegexesType) => async (dispatch, getState) => {
    const state: ReduxState = getState();
    if (state.TagRegexes.loading) {
      return;
    }
    dispatch(setLoading(true));

    try {
      const { tagRegexes, tagRegexesSets } = await getTagRegexes(type);
      dispatch(setTagRegexes(tagRegexes));
      dispatch(setTagRegexesSets(tagRegexesSets));
      dispatch(setLoading(false));
      // }
    } catch (err) {
      dispatch(setLoading(false));
    }
  };

export const fetchPieChart = ({ selectedAccount, l1, l2 }) => async (dispatch, getState) => {
  const state: ReduxState = getState();

  try {
    if (state.TagRegexes.pieLoading) {
      return;
    }
    dispatch(setPieLoading(true));
    const jobId = state?.JobData?.job?.id;

    let result;
    if (l1 && l2) {
      result = await getCategorizedTransactionsWithTagLevels(
        l1, l2,
        selectedAccount
      );
    } else if (jobId) {
      result = await getCategorizedTransactions(
        jobId,
        selectedAccount
      );
    }
    const { transactions, categories, byCategoryKeyword } = result;

    dispatch(setTls({ transactions, resetPages: false }));
    dispatch(
      setCategories(
        categories
          .sort((a, b) => a.name.localeCompare(b.name))
          .map((c, i) => ({
            ...c,
            color: randomColor(i),
          }))
      )
    );
    dispatch(
      setByCategoryKeyword(
        byCategoryKeyword
          .sort((a, b) => a.name.localeCompare(b.name))
          .map((c, i) => ({
            ...c,
            color: randomColor(i + categories.length),
          }))
      )
    );
    dispatch(setPieLoading(false));
  } catch (err) {
    dispatch(setPieLoading(false));
  }
};

export const updateTagRegexesAction =
  (id, tag, tagLabel, regexes, rule) => async (dispatch, getState) => {
    try {
      updateTagRegexes(id, tag, tagLabel, regexes, rule)
        .then((res) => {
          dispatch(updateTagRegexes2({ id, tag, tagLabel, regexes, jsonRules: res.jsonRules }));
        })
        .catch((err) => dispatch(createAlert({
          message: err.message || "Error updating tag regex. Please try again.",
          timeout: 0,
          type: "error",
        })));
    } catch (err) { }
  };

export const deleteTagRegexesAction = (id) => async (dispatch, getState) => {
  try {
    deleteTagRegexes(id)
      .then(() => {
        dispatch(
          createAlert({
            message: "Rule deleted successfully",
            timeout: 3000,
            type: "success",
          })
        );
      })
      .catch((err) => dispatch(createAlert({
        message: err.message || "Error deleting rule. Please try again.",
        timeout: 0,
        type: "error",
      })));
  } catch (err) { }
}

export const updateTagRuleAction =
  (rule: TagRule) => async (dispatch, getState) => {
    try {
      updateTagRule(rule)
        .then(() => {
          dispatch(updateTagRule2(rule));
        })
        .catch((err) => dispatch(createAlert({
          message: err.message || "Error updating tag rule. Please try again.",
          timeout: 0,
          type: "error",
        })));
    } catch (err) { }
  };

export const updateTagCategoryRuleAction =
  (rule: TagRule) => async (dispatch, getState) => {
    try {
      updateTagRule(rule)
        .then(() => {
          dispatch(updateTagRule2(rule));
        })
        .catch((err) => dispatch(createAlert({
          message: err.message || "Error updating tag category rule. Please try again.",
          timeout: 0,
          type: "error",
        })));
    } catch (err) { }
  };

export const addNewTag = (set, tag, tagLabel, regexes, rule: TagCategoryRule) => async (dispatch, getState) => {
  try {
    createTagRegexWithSet(set, tag, tagLabel, "[]", rule, "categorize")
      .then((rsp) => {
        // console.log("addNewTag -> createTagRegexWithSet -> rsp", rsp);
        dispatch(
          addTagRegexes({
            id: rsp.tagRegex.id,
            regexes: rsp.tagRegex.regexes,
            tag: rsp.tagRegex.tag,
            tagLabel: rsp.tagRegex.tagLabel,
            jsonRules: rsp.tagRegex.jsonRules
          })
        );
        dispatch(addTagRegexesSet(rsp.tagRegexSet));

        // If there are keywords, update the tag with them
        if (rule.keywords.length > 0 || rule.negativeKeywords.length > 0) {
          dispatch(updateTagRegexesAction(
            rsp.tagRegex.id,
            rsp.tagRegex.tag,
            rsp.tagRegex.tagLabel,
            JSON.stringify(rule.keywords),
            rule
          ));
        }
      })
      .catch((err) => dispatch(createAlert({
        message: err.message || "Error creating tag regex with set. Please try again.",
        timeout: 0,
        type: "error",
      })));
  } catch (err) { }
};

export const addTagRule = (rule: TagRule) => async (dispatch, getState) => {
  const state: ReduxState = getState();

  try {
    createTagRule(rule)
      .then((rsp) => {
        dispatch(addTagRule2(rsp));
        createTagRegexesSet(
          state.TagRegexes.selectedTagRegexesSet,
          rsp.id,
          "tag"
        )
          .then((rsp2) => {
            dispatch(addTagRegexesSet(rsp2));
          })
          .catch((err) => dispatch(createAlert({
            message: err.message || "Error adding rules. Please try again.",
            timeout: 0,
            type: "error",
          })));
      })
      .catch((err) => dispatch(createAlert({
        message: err.message || "Error creating tag rule. Please try again.",
        timeout: 0,
        type: "error",
      })));
  } catch (err) { }
};

export const addNewCategoryGroup = (name, rule: TagCategoryRule) => async (dispatch, getState) => {
  if (!name) {
    dispatch(
      createAlert({
        message: "Need to specify the category group name",
        timeout: 0,
        type: "error",
      })
    );
    return;
  }
  try {
    createTagRegexWithSet(name, `${name} - default category`, "", "[]", rule, "categorize")
      .then((rsp) => {
        dispatch(
          addTagRegexes({
            id: rsp.tagRegex.id,
            regexes: rsp.tagRegex.regexes,
            tag: rsp.tagRegex.tag,
            tagLabel: rsp.tagRegex.tagLabel,
            jsonRules: rsp.tagRegex.jsonRules
          })
        );
        dispatch(addTagRegexesSet(rsp.tagRegexSet));
        dispatch(setSelectedTagRegexesSet(rsp.tagRegexSet.name));
      })
      .catch((err) => dispatch(createAlert({
        message: err.message || "Error creating category group. Please try again.",
        timeout: 0,
        type: "error",
      })));
  } catch (err) { }
};

export const cloneCategoryGroup = (
  sourceName: string,
  newName: string
) => async (dispatch, getState) => {
  if (!sourceName || !newName) {
    dispatch(
      createAlert({
        message: "Need to specify both source and new group names",
        timeout: 0,
        type: "error",
      })
    );
    return;
  }
  try {
    await cloneTagRegexesSet(sourceName, newName, "categorize");
    // Re-pull the full set so the new cloned group + all its categories
    // show up in the UI. Simpler than reconstructing them locally.
    const { tagRegexes, tagRegexesSets } = await getTagRegexes("categorize");
    dispatch(setTagRegexes(tagRegexes));
    dispatch(setTagRegexesSets(tagRegexesSets));
    dispatch(setSelectedTagRegexesSet(newName));
  } catch (err) {
    dispatch(
      createAlert({
        message: (err && (err as any).message) || "Error cloning category group. Please try again.",
        timeout: 0,
        type: "error",
      })
    );
  }
};

export default slice.reducer;
